'use client'

import { Button } from '@/components/ui/Button'
import Container from '@/components/ui/Container'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { sendGTMEvent } from '@next/third-parties/google'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useState } from 'react'
import MobileHeaderItem from './MobileHeaderItem'
import { SearchModal } from './SearchModal'
import ThemeToggle from './ThemeToggle'

export default function Header({ userData }) {
  const pathname = usePathname()
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  const handleSubscribeClick = () => {
    sendGTMEvent({
      event: 'subscribe_button_clicked',
      tracking_location: 'header',
    })
  }

  // Navigation items
  const navItems = [
    { href: '/how-it-works', label: 'How It Works' },
    { href: '/sources', label: 'Sources' },
    { href: '/about', label: 'About' },
    { href: '/faq', label: 'FAQ' },
  ]

  return (
    <>
      <Disclosure
        as="nav"
        className="relative border-b-2 border-foreground bg-background"
      >
        <Container>
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center">
              {/* Mobile menu button */}
              <div className="mr-3 -ml-2 flex items-center md:hidden">
                <DisclosureButton className="group relative inline-flex items-center justify-center p-2 text-muted-foreground transition-colors hover:text-foreground focus:outline-none">
                  <span className="sr-only">Open main menu</span>
                  <Bars3Icon
                    aria-hidden="true"
                    className="block size-6 group-data-open:hidden"
                  />
                  <XMarkIcon
                    aria-hidden="true"
                    className="hidden size-6 group-data-open:block"
                  />
                </DisclosureButton>
              </div>

              {/* Logo */}
              <Link href="/" className="transition-opacity hover:opacity-80">
                <Image
                  alt="Pano News"
                  src="/logo.png"
                  width={400}
                  height={157}
                  className="h-8 w-auto dark:hidden"
                  priority
                />
                <Image
                  alt="Pano News"
                  src="/logo-dark.png"
                  width={400}
                  height={157}
                  className="hidden h-8 w-auto dark:block"
                  priority
                />
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:ml-8 md:flex md:items-center md:gap-1">
                {navItems.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={`px-3 py-2 font-sans text-sm transition-colors hover:text-foreground ${
                      pathname === item.href
                        ? 'font-semibold text-foreground'
                        : 'text-muted-foreground'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* Right side items */}
            <div className="flex items-center gap-2">
              {/* Search Button - Desktop only */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="hidden md:block p-2 text-muted-foreground transition-colors hover:text-foreground focus:outline-none"
                aria-label="Search stories"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
              </button>

              {/* Theme Toggle - Desktop */}
              <div className="hidden md:block">
                <ThemeToggle />
              </div>

              {/* Auth Buttons - Desktop */}
              <div className="hidden md:flex items-center gap-2">
                {userData?.type === 'guest' ? (
                  <>
                    <Button href="/login" variant="ghost" size="sm">
                      Login
                    </Button>
                    <Button
                      href="/subscribe"
                      variant="primary"
                      size="sm"
                      onClick={handleSubscribeClick}
                    >
                      Subscribe
                    </Button>
                  </>
                ) : (
                  <Button href="/account" variant="ghost" size="sm">
                    Account
                  </Button>
                )}
              </div>

              {/* Auth Button - Mobile */}
              <div className="md:hidden">
                {userData?.type === 'guest' ? (
                  <Button
                    href="/subscribe"
                    variant="primary"
                    size="sm"
                    onClick={handleSubscribeClick}
                  >
                    Subscribe
                  </Button>
                ) : (
                  <Button href="/account" variant="ghost" size="sm">
                    Account
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Container>

        {/* Mobile menu */}
        <DisclosurePanel className="border-t border-border md:hidden">
          <Container>
            {/* Search button */}
            <div className="py-3 border-b border-border">
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex items-center gap-3 w-full px-3 py-2 font-sans text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <MagnifyingGlassIcon className="h-5 w-5" />
                Search stories
              </button>
            </div>

            <div className="py-3 space-y-1">
              {navItems.map((item) => (
                <MobileHeaderItem
                  key={item.href}
                  href={item.href}
                  isActive={pathname === item.href}
                >
                  {item.label}
                </MobileHeaderItem>
              ))}
              <MobileHeaderItem
                href="/contact-us"
                isActive={pathname === '/contact-us'}
              >
                Contact
              </MobileHeaderItem>
            </div>

            {/* Mobile Auth Links */}
            <div className="border-t border-border py-3">
              {userData?.type === 'guest' ? (
                <MobileHeaderItem
                  href="/login"
                  isActive={pathname === '/login'}
                >
                  Login
                </MobileHeaderItem>
              ) : (
                <MobileHeaderItem
                  href="/account"
                  isActive={pathname === '/account'}
                >
                  Account
                </MobileHeaderItem>
              )}
            </div>

            {/* Mobile theme toggle */}
            <div className="border-t border-border py-4">
              <div className="flex items-center justify-between">
                <span className="font-sans text-sm text-muted-foreground">Theme</span>
                <ThemeToggle />
              </div>
            </div>
          </Container>
        </DisclosurePanel>
      </Disclosure>

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />
    </>
  )
}
